import React, { useEffect, useMemo, useRef, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { GridColDef } from '@mui/x-data-grid';
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { portfoliosApi, PortfolioListItem, ppmProjectsApi, PpmProjectListItem, roadmapsApi, RoadmapListItem, roadmapItemsApi, RoadmapItemListItem } from '../api/ppm';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens } from '../theme/tokens';

function statusColor(s?: string | null) {
  const v = String(s || '').toUpperCase();
  if (v === 'ACTIVE') return 'primary';
  if (v === 'COMPLETED') return 'success';
  if (v === 'ON_HOLD') return 'warning';
  return 'default';
}

function daysBetween(a?: string | null, b?: string | null) {
  if (!a || !b) return null;
  const start = new Date(a).getTime();
  const end = new Date(b).getTime();
  if (!start || !end) return null;
  const d = Math.round((end - start) / (1000 * 60 * 60 * 24));
  return isNaN(d) ? null : d;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function startOfMonth(d: Date) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function addMonths(d: Date, count: number) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + count, 1));
}

function fmtYmd(d?: any) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

function monthLabel(d: Date) {
  return d.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

function quarterLabel(d: Date) {
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  return `Q${q}`;
}

function prettyEnumLabel(v: any) {
  return String(v || '')
    .replace(/_/g, ' ')
    .trim();
}

const RECENT_COLORS_KEY = 'roadmaps.recentColors.v1';
// App palette: blue + neutrals, with red available for critical states.
const DEFAULT_COLORS = ['#2563EB', '#1D4ED8', '#60A5FA', '#0B1220', '#94A3B8', '#06B6D4', '#EF4444', '#22C55E'];

function normalizeHexColor(v: string) {
  const s = String(v || '').trim();
  if (!s) return '';
  const withHash = s.startsWith('#') ? s : `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) return withHash.toUpperCase();
  return s;
}

function readRecentColors(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_COLORS_KEY);
    const arr = raw ? (JSON.parse(raw) as any) : [];
    const cleaned = (Array.isArray(arr) ? arr : []).map((x) => normalizeHexColor(String(x))).filter((x) => /^#[0-9A-F]{6}$/.test(x));
    return Array.from(new Set(cleaned)).slice(0, 10);
  } catch {
    return [];
  }
}

function writeRecentColors(colors: string[]) {
  try {
    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(colors.slice(0, 10)));
  } catch {
    // ignore
  }
}

const RoadmapsPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'roadmaps');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const timelineRef = useRef<HTMLDivElement | null>(null);

  const [topPanel, setTopPanel] = useState<null | 'roadmaps' | 'inFlight' | 'lanes' | 'upcoming'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const [portfolios, setPortfolios] = useState<PortfolioListItem[]>([]);
  // The old top-level Portfolio filter + Upcoming window controls were removed (they were confusing/non-functional).
  const [days] = useState(30);

  const [roadmaps, setRoadmaps] = useState<RoadmapListItem[]>([]);
  const [upcoming, setUpcoming] = useState<any[]>([]);
  const [projects, setProjects] = useState<PpmProjectListItem[]>([]);
  const [view, setView] = useState<'timeline' | 'data'>('timeline');
  const [selectedRoadmapId, setSelectedRoadmapId] = useState<string>('');
  const [zoom, setZoom] = useState<'quarters' | 'months'>('quarters');

  // Top stat-card drilldown (used for "In flight" + "Lanes" panels)
  const [topPanelRoadmapId, setTopPanelRoadmapId] = useState<string>('');

  const [roadmapDialogOpen, setRoadmapDialogOpen] = useState(false);
  const [editingRoadmap, setEditingRoadmap] = useState<RoadmapListItem | null>(null);
  const [roadmapForm, setRoadmapForm] = useState({
    name: '',
    description: '',
    portfolioId: '',
    startDate: '',
    endDate: '',
    viewType: 'timeline',
  });
  const [savingRoadmap, setSavingRoadmap] = useState(false);

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItemListItem | null>(null);
  const [itemForm, setItemForm] = useState({
    roadmapId: '',
    projectId: '',
    title: '',
    description: '',
    lane: '',
    status: 'PLANNED',
    color: '#3B82F6',
    startDate: '',
    endDate: '',
  });
  const [savingItem, setSavingItem] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pRes, rRes, uRes] = await Promise.all([
        portfoliosApi.getAll(),
        roadmapsApi.getAll(),
        roadmapsApi.getUpcomingMilestones({ days }),
      ]);
      setPortfolios((pRes.data || []) as PortfolioListItem[]);
      const rms = (rRes.data || []) as any[];
      setRoadmaps(rms as any);
      setUpcoming(uRes.data || []);
      setSelectedRoadmapId((prev) => {
        if (prev && rms.some((r) => String(r.id) === String(prev))) return prev;
        // default to current-year roadmap if available (otherwise first)
        const now = new Date();
        const y = now.getFullYear();
        const currentYear = rms.find((r) => {
          const s = r?.startDate ? new Date(r.startDate) : null;
          return !!s && !Number.isNaN(s.getTime()) && s.getUTCFullYear() === y;
        });
        return String(currentYear?.id || rms[0]?.id || '');
      });
      // default zoom based on span (high-level = quarters)
      try {
        const r0: any = rms.find((r) => String(r.id) === String(selectedRoadmapId || rms[0]?.id));
        const start = r0?.startDate ? new Date(r0.startDate) : null;
        const end = r0?.endDate ? new Date(r0.endDate) : null;
        if (start && end) {
          const days = Math.abs((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
          setZoom(days > 140 ? 'quarters' : 'months');
        }
      } catch {
        // ignore
      }
      try {
        const projRes = await ppmProjectsApi.getAll({ source: 'internal' });
        setProjects(projRes.data || []);
      } catch {
        setProjects([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load roadmaps');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  useEffect(() => {
    // keep selection valid when filters change
    if (!roadmaps.length) {
      setSelectedRoadmapId('');
      return;
    }
    if (selectedRoadmapId && roadmaps.some((r) => String(r.id) === String(selectedRoadmapId))) return;
    setSelectedRoadmapId(String((roadmaps as any)[0]?.id || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadmaps]);

  const selectedRoadmap = useMemo(() => {
    const r: any = (roadmaps as any[]).find((x) => String(x.id) === String(selectedRoadmapId));
    return r || null;
  }, [roadmaps, selectedRoadmapId]);

  const selectedItems = useMemo(() => {
    const items: any[] = (selectedRoadmap?.Items || selectedRoadmap?.items || []) as any[];
    return items.map((it) => ({
      id: String(it.id),
      roadmapId: String(it.roadmapId || selectedRoadmap?.id || ''),
      projectId: it.projectId ? String(it.projectId) : null,
      title: String(it.title || it.name || ''),
      description: it.description || null,
      startDate: it.startDate ? String(it.startDate) : null,
      endDate: it.endDate ? String(it.endDate) : null,
      color: it.color || null,
      lane: it.lane || null,
      status: it.status || null,
    })) as RoadmapItemListItem[];
  }, [selectedRoadmap]);

  const totals = useMemo(() => {
    const total = roadmaps.length;
    const now = new Date();
    const allItems: any[] = (roadmaps as any[]).flatMap((r: any) => (r.Items || []) as any[]);
    const inFlight = allItems.filter((it) => {
      const s = it.startDate ? new Date(it.startDate) : null;
      const e = it.endDate ? new Date(it.endDate) : null;
      if (!s || !e) return false;
      return s.getTime() <= now.getTime() && e.getTime() >= now.getTime();
    }).length;
    const milestones = upcoming.length;
    const next = upcoming[0]?.endDate || null;
    // Count lanes the same way the drilldown does: sum of unique lanes per roadmap
    // (not the union of lane names across all roadmaps).
    const lanes = (roadmaps as any[]).reduce((sum: number, rm: any) => {
      const items: any[] = (rm?.Items || rm?.items || []) as any[];
      const s = new Set(items.map((it) => String(it?.lane || 'General')));
      return sum + s.size;
    }, 0);
    return { total, inFlight, milestones, lanes, next };
  }, [roadmaps, upcoming]);

  const timeline = useMemo(() => {
    const now = new Date();
    const rm: any = selectedRoadmap;
    const start = rm?.startDate ? new Date(rm.startDate) : startOfMonth(now);
    const end = rm?.endDate ? new Date(rm.endDate) : addMonths(startOfMonth(now), 3);
    const rangeStart = startOfMonth(start);
    const rangeEnd = addMonths(startOfMonth(end), 1); // include last month

    // Build segments
    const segs: Array<{ label: string; start: Date; end: Date }> = [];
    if (zoom === 'quarters') {
      const qStart = new Date(Date.UTC(rangeStart.getUTCFullYear(), Math.floor(rangeStart.getUTCMonth() / 3) * 3, 1));
      for (let d = qStart; d.getTime() < rangeEnd.getTime(); d = addMonths(d, 3)) {
        const s = d;
        const e = addMonths(d, 3);
        segs.push({ label: quarterLabel(s), start: s, end: e });
      }
    } else {
      for (let d = rangeStart; d.getTime() < rangeEnd.getTime(); d = addMonths(d, 1)) {
        const s = d;
        const e = addMonths(d, 1);
        segs.push({ label: monthLabel(s), start: s, end: e });
      }
    }

    const msStart = rangeStart.getTime();
    const msEnd = rangeEnd.getTime();
    const span = Math.max(1, msEnd - msStart);

    // Pack items into multiple rows per lane to avoid overlapping bars.
    const laneMap = new Map<string, RoadmapItemListItem[]>();
    selectedItems.forEach((it) => {
      const lane = String(it.lane || 'General');
      if (!laneMap.has(lane)) laneMap.set(lane, []);
      laneMap.get(lane)!.push(it);
    });

    const packedLanes = Array.from(laneMap.entries())
      .map(([lane, items]) => {
        const normalized = items
          .map((it) => {
            const s = it.startDate ? new Date(it.startDate).getTime() : msStart;
            const e = it.endDate ? new Date(it.endDate).getTime() : s + 7 * 24 * 60 * 60 * 1000;
            const startMs = clamp(s, msStart, msEnd - 1);
            const endMs = clamp(e, startMs + 1, msEnd);
            return { it, startMs, endMs };
          })
          .sort((a, b) => a.startMs - b.startMs);

        // greedy interval packing
        const rowEnds: number[] = [];
        const rows: Array<Array<{ it: RoadmapItemListItem; startMs: number; endMs: number; row: number }>> = [];

        normalized.forEach((x) => {
          let row = rowEnds.findIndex((endMs) => x.startMs >= endMs);
          if (row === -1) {
            row = rowEnds.length;
            rowEnds.push(x.endMs);
            rows.push([]);
          } else {
            rowEnds[row] = x.endMs;
          }
          rows[row].push({ it: x.it, startMs: x.startMs, endMs: x.endMs, row });
        });

        const flat = rows.flat();
        const rowCount = Math.max(1, rows.length);
        return { lane, items: flat, rowCount };
      })
      .sort((a, b) => a.lane.localeCompare(b.lane));

    const segWidth = zoom === 'quarters' ? 320 : 170; // quarters should feel roomy; scroll for more
    const totalWidthPx = segs.length * segWidth;
    return { rangeStart, rangeEnd, segs, segWidth, totalWidthPx, msStart, msEnd, span, lanes: packedLanes };
  }, [selectedRoadmap, selectedItems, zoom]);

  const laneOptions = useMemo(() => {
    const s = new Set<string>();
    selectedItems.forEach((it: any) => s.add(String(it.lane || 'General')));
    if (!s.size) s.add('General');
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [selectedItems]);

  const NEW_LANE_VALUE = '__new_lane__';
  const [laneCreateMode, setLaneCreateMode] = useState(false);
  const [newLaneName, setNewLaneName] = useState('');

  const [recentColors, setRecentColors] = useState<string[]>(() => readRecentColors());

  const [milestonesMode, setMilestonesMode] = useState<'upcoming' | 'all'>('upcoming');
  const [milestonesRoadmapId, setMilestonesRoadmapId] = useState<string>('');

  useEffect(() => {
    setMilestonesRoadmapId((prev) => prev || selectedRoadmapId || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoadmapId]);

  const openNewRoadmap = () => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), 11, 31));
    setEditingRoadmap(null);
    setRoadmapForm({
      name: '',
      description: '',
      portfolioId: portfolios[0]?.id || '',
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
      viewType: 'timeline',
    });
    setRoadmapDialogOpen(true);
  };

  const openEditRoadmap = (r: RoadmapListItem) => {
    setEditingRoadmap(r);
    setRoadmapForm({
      name: (r as any).name || (r as any).title || '',
      description: (r as any).description || '',
      portfolioId: r.portfolioId || '',
      startDate: (r as any).startDate ? String((r as any).startDate).slice(0, 10) : '',
      endDate: (r as any).endDate ? String((r as any).endDate).slice(0, 10) : '',
      viewType: (r as any).viewType || 'timeline',
    });
    setRoadmapDialogOpen(true);
  };

  const saveRoadmap = async () => {
    try {
      setSavingRoadmap(true);
      const payload: any = {
        name: roadmapForm.name,
        description: roadmapForm.description || undefined,
        viewType: roadmapForm.viewType || 'timeline',
        startDate: roadmapForm.startDate ? new Date(`${roadmapForm.startDate}T00:00:00.000Z`) : new Date(),
        endDate: roadmapForm.endDate ? new Date(`${roadmapForm.endDate}T00:00:00.000Z`) : new Date(),
        ...(roadmapForm.portfolioId ? { Portfolio: { connect: { id: roadmapForm.portfolioId } } } : { portfolioId: null }),
      };

      if (editingRoadmap?.id) {
        await roadmapsApi.update(editingRoadmap.id, payload);
      } else {
        await roadmapsApi.create(payload);
      }
      setRoadmapDialogOpen(false);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save roadmap');
    } finally {
      setSavingRoadmap(false);
    }
  };

  const deleteRoadmap = async (id: string) => {
    try {
      await roadmapsApi.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete roadmap');
    }
  };

  const openNewItem = () => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
    setLaneCreateMode(false);
    setNewLaneName('');
    setEditingItem(null);
    setItemForm({
      roadmapId: roadmaps[0]?.id || '',
      projectId: '',
      title: '',
      description: '',
      lane: laneOptions[0] || 'General',
      status: 'PLANNED',
      color: '#3B82F6',
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    });
    setItemDialogOpen(true);
  };

  const openEditItem = (row: any) => {
    const r = row as RoadmapItemListItem;
    setLaneCreateMode(false);
    setNewLaneName('');
    setEditingItem(r);
    setItemForm({
      roadmapId: r.roadmapId || r.Roadmap?.id || '',
      projectId: r.projectId || r.Project?.id || '',
      title: r.title || '',
      description: r.description || '',
      lane: r.lane || 'General',
      status: r.status || 'PLANNED',
      color: r.color || '#3B82F6',
      startDate: r.startDate ? String(r.startDate).slice(0, 10) : '',
      endDate: r.endDate ? String(r.endDate).slice(0, 10) : '',
    });
    setItemDialogOpen(true);
  };

  const saveItem = async () => {
    try {
      setSavingItem(true);
      const lane = (itemForm.lane || '').trim() || 'General';
      const pickedColor = normalizeHexColor(itemForm.color || '');
      const payload: any = {
        title: itemForm.title,
        description: itemForm.description || undefined,
        lane,
        status: itemForm.status || undefined,
        color: pickedColor || undefined,
        startDate: itemForm.startDate ? new Date(`${itemForm.startDate}T00:00:00.000Z`) : new Date(),
        endDate: itemForm.endDate ? new Date(`${itemForm.endDate}T00:00:00.000Z`) : new Date(),
        ...(itemForm.projectId ? { Project: { connect: { id: itemForm.projectId } } } : {}),
      };

      if (editingItem?.id) {
        await roadmapItemsApi.update(editingItem.id, payload);
      } else {
        await roadmapItemsApi.create(itemForm.roadmapId, payload);
      }
      if (pickedColor && /^#[0-9A-F]{6}$/.test(pickedColor)) {
        setRecentColors((prev) => {
          const next = [pickedColor, ...prev.filter((c) => c !== pickedColor)];
          writeRecentColors(next);
          return next;
        });
      }
      setItemDialogOpen(false);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save roadmap item');
    } finally {
      setSavingItem(false);
    }
  };

  const deleteItem = async (id: string) => {
    try {
      await roadmapItemsApi.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete roadmap item');
    }
  };

  const roadmapCols: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Roadmap',
      flex: 1.2,
      minWidth: 260,
      valueGetter: (params) => {
        const r: any = params.row;
        return r.title || r.name || 'Untitled';
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          variant="outlined"
          label={prettyEnumLabel(params.value || '—')}
          color={statusColor(String(params.value || '')) as any}
          sx={{ height: 22, fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'portfolioId',
      headerName: 'Portfolio',
      width: 220,
      valueGetter: (params) => portfolios.find((p) => p.id === String(params.value))?.name || '—',
    },
    {
      field: 'duration',
      headerName: 'Duration',
      minWidth: 220,
      flex: 0.9,
      valueGetter: (params) => {
        const r: any = params.row;
        return daysBetween(r.startDate, r.endDate);
      },
      renderCell: (params) => {
        const d = params.value as any;
        if (d == null) return <Typography variant="caption" color="text.secondary">—</Typography>;
        const v = Math.max(0, Math.min(100, (Number(d) / 180) * 100)); // normalize to ~6 months
        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {d}d
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
                {String(params.row?.startDate || '').slice(0, 10)} → {String(params.row?.endDate || '').slice(0, 10)}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={v} sx={{ height: 8, borderRadius: 999, backgroundColor: tokens.color.neutralSoft }} />
          </Box>
        );
      },
    },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: '',
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Stack direction="row" spacing={0.5}>
                <Button size="small" variant="text" onClick={() => openEditRoadmap(params.row as any)} sx={{ fontWeight: 650 }}>
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  onClick={() => deleteRoadmap(String((params.row as any).id))}
                  sx={{ fontWeight: 650 }}
                >
                  Delete
                </Button>
              </Stack>
            ),
          },
        ] as GridColDef[])
      : []),
  ];

  const milestoneCols: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Milestone / Item',
      flex: 1.2,
      minWidth: 260,
      valueGetter: (params) => {
        const r: any = params.row;
        return r.title || r.name || 'Untitled item';
      },
    },
    { field: 'endDate', headerName: 'Due', width: 130, valueGetter: (params) => ((params.row as any)?.endDate ? String((params.row as any).endDate).slice(0, 10) : '—') },
    { field: 'status', headerName: 'Status', width: 140, valueGetter: (params) => prettyEnumLabel((params.row as any)?.status || '—') },
    {
      field: 'roadmap',
      headerName: 'Roadmap',
      width: 220,
      valueGetter: (params) => (params.row as any)?.Roadmap?.name || '—',
    },
    {
      field: 'project',
      headerName: 'Project',
      width: 220,
      valueGetter: (params) => (params.row as any)?.Project?.name || '—',
    },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: '',
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Stack direction="row" spacing={0.5}>
                <Button size="small" variant="text" onClick={() => openEditItem(params.row as any)} sx={{ fontWeight: 650 }}>
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  onClick={() => deleteItem(String((params.row as any).id))}
                  sx={{ fontWeight: 650 }}
                >
                  Delete
                </Button>
              </Stack>
            ),
          },
        ] as GridColDef[])
      : []),
  ];

  const allMilestonesRows = useMemo(() => {
    const rms: any[] = (roadmaps as any[]) || [];
    const rows = rms.flatMap((rm: any) => {
      const items: any[] = (rm?.Items || rm?.items || []) as any[];
      return items.map((it, idx) => ({
        id: String(it?.id || `${rm?.id || 'rm'}_${idx}`),
        ...it,
        Roadmap: it?.Roadmap || rm,
        roadmapId: String(it?.roadmapId || rm?.id || ''),
      }));
    });
    const wanted = String(milestonesRoadmapId || '');
    if (!wanted) return rows;
    return rows.filter((r) => String((r as any).roadmapId || (r as any)?.Roadmap?.id || '') === wanted);
  }, [roadmaps, milestonesRoadmapId]);

  // NOTE: lane + in-flight calculations for the top stat-card panels are now done
  // via `lanesByRoadmap` and `inFlightByRoadmap` (plus per-roadmap derived lists),
  // so we no longer compute a global laneCounts/inFlightItems list here.

  const topPanelRoadmap = useMemo(() => {
    if (!topPanelRoadmapId) return null;
    const r: any = (roadmaps as any[]).find((x) => String(x.id) === String(topPanelRoadmapId));
    return r || null;
  }, [roadmaps, topPanelRoadmapId]);

  const inFlightByRoadmap = useMemo(() => {
    const now = Date.now();
    return (roadmaps as any[])
      .map((rm: any) => {
        const items: any[] = (rm?.Items || rm?.items || []) as any[];
        const count = items.filter((it) => {
          const s = it?.startDate ? new Date(it.startDate).getTime() : NaN;
          const e = it?.endDate ? new Date(it.endDate).getTime() : NaN;
          return Number.isFinite(s) && Number.isFinite(e) && s <= now && e >= now;
        }).length;
        return { id: String(rm?.id || ''), name: String(rm?.name || rm?.title || rm?.id || 'Roadmap'), count };
      })
      .filter((x) => x.id && x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [roadmaps]);

  const lanesByRoadmap = useMemo(() => {
    return (roadmaps as any[])
      .map((rm: any) => {
        const items: any[] = (rm?.Items || rm?.items || []) as any[];
        const lanes = new Set(items.map((it) => String(it?.lane || 'General')));
        return { id: String(rm?.id || ''), name: String(rm?.name || rm?.title || rm?.id || 'Roadmap'), count: lanes.size };
      })
      .filter((x) => x.id && x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [roadmaps]);

  const itemsByRoadmap = useMemo(() => {
    return (roadmaps as any[])
      .map((rm: any) => {
        const items: any[] = (rm?.Items || rm?.items || []) as any[];
        return { id: String(rm?.id || ''), name: String(rm?.name || rm?.title || rm?.id || 'Roadmap'), count: items.length };
      })
      .filter((x) => x.id && x.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [roadmaps]);

  const itemStatusMix = useMemo(() => {
    const map = new Map<string, number>();
    (roadmaps as any[]).forEach((rm: any) => {
      const items: any[] = (rm?.Items || rm?.items || []) as any[];
      items.forEach((it) => {
        const s = String(it?.status || 'UNSPECIFIED').toUpperCase();
        map.set(s, (map.get(s) || 0) + 1);
      });
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [roadmaps]);

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Roadmaps"
        subtitle="High-level portfolio roadmaps with swimlanes and timeline bars."
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            {canEdit ? (
              <Button size="small" variant="contained" onClick={openNewRoadmap} sx={{ borderRadius: 2 }}>
                New roadmap
              </Button>
            ) : null}
          </Box>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Roadmaps"
            value={totals.total}
            subtitle="Configured roadmaps"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'roadmaps' ? null : 'roadmaps'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="In flight"
            value={totals.inFlight}
            subtitle="Items active right now"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanelRoadmapId('');
              setTopPanel((p) => (p === 'inFlight' ? null : 'inFlight'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Lanes"
            value={totals.lanes}
            subtitle="Swimlanes in use"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanelRoadmapId('');
              setTopPanel((p) => (p === 'lanes' ? null : 'lanes'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Upcoming"
            value={totals.milestones}
            subtitle={`Next ${days} days`}
            accent={tokens.color.warning}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'upcoming' ? null : 'upcoming'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={
                topPanel === 'roadmaps'
                  ? 'Roadmaps'
                  : topPanel === 'inFlight'
                    ? topPanelRoadmap ? `In flight • ${String(topPanelRoadmap.name || topPanelRoadmap.title || topPanelRoadmap.id)}` : 'In flight'
                    : topPanel === 'lanes'
                      ? topPanelRoadmap ? `Lanes • ${String(topPanelRoadmap.name || topPanelRoadmap.title || topPanelRoadmap.id)}` : 'Lanes'
                      : 'Upcoming milestones'
              }
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  {topPanelRoadmapId && (topPanel === 'inFlight' || topPanel === 'lanes') ? (
                    <Button size="small" variant="outlined" onClick={() => setTopPanelRoadmapId('')} sx={{ borderRadius: 2 }}>
                      Back
                    </Button>
                  ) : null}
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setTopPanel(null);
                      setTopPanelRoadmapId('');
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    Close
                  </Button>
                </Stack>
              }
            >
              {topPanel === 'roadmaps' ? (
                <Stack spacing={1}>
                  {(roadmaps as any[]).slice(0, showAllPanel ? 1000 : 5).map((r: any) => (
                    <Box
                      key={r.id}
                      sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                      onClick={() => {
                        setSelectedRoadmapId(String(r.id));
                        setView('timeline');
                        setTopPanel(null);
                        requestAnimationFrame(() => timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                      }}
                    >
                      <Typography sx={{ fontWeight: 650 }} noWrap>
                        {r.name || r.title || String(r.id)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Range {fmtYmd(r.startDate)} → {fmtYmd(r.endDate)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : null}

              {topPanel === 'lanes' ? (
                !topPanelRoadmapId ? (
                  lanesByRoadmap.length ? (
                    <Stack spacing={1}>
                      {lanesByRoadmap.slice(0, showAllPanel ? 1000 : 5).map((rm) => (
                        <Box
                          key={rm.id}
                          sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                          onClick={() => {
                            setTopPanelRoadmapId(rm.id);
                            setShowAllPanel(false);
                          }}
                        >
                          <Typography sx={{ fontWeight: 650 }} noWrap>
                            {rm.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {rm.count} lane(s)
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info" variant="outlined">
                      No lanes found.
                    </Alert>
                  )
                ) : (() => {
                    const items: any[] = (topPanelRoadmap?.Items || topPanelRoadmap?.items || []) as any[];
                    const m = new Map<string, number>();
                    items.forEach((it) => {
                      const lane = String(it?.lane || 'General');
                      m.set(lane, (m.get(lane) || 0) + 1);
                    });
                    const list = Array.from(m.entries()).sort((a, b) => b[1] - a[1]);

                    return list.length ? (
                      <Stack spacing={1}>
                        {list.slice(0, showAllPanel ? 1000 : 5).map(([lane, count]) => (
                          <Box
                            key={lane}
                            sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                            onClick={() => {
                              setSelectedRoadmapId(String(topPanelRoadmapId));
                              setView('timeline');
                              setTopPanel(null);
                              setTopPanelRoadmapId('');
                              requestAnimationFrame(() => timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                            }}
                          >
                            <Typography sx={{ fontWeight: 650 }} noWrap>
                              {lane}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {count} item(s)
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info" variant="outlined">
                        No lanes found for this roadmap.
                      </Alert>
                    );
                  })()
              ) : null}

              {topPanel === 'inFlight' ? (
                !topPanelRoadmapId ? (
                  inFlightByRoadmap.length ? (
                    <Stack spacing={1}>
                      {inFlightByRoadmap.slice(0, showAllPanel ? 1000 : 5).map((rm) => (
                        <Box
                          key={rm.id}
                          sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                          onClick={() => {
                            setTopPanelRoadmapId(rm.id);
                            setShowAllPanel(false);
                          }}
                        >
                          <Typography sx={{ fontWeight: 650 }} noWrap>
                            {rm.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {rm.count} in flight
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info" variant="outlined">
                      No items are currently in flight.
                    </Alert>
                  )
                ) : (() => {
                    const now = Date.now();
                    const items: any[] = (topPanelRoadmap?.Items || topPanelRoadmap?.items || []) as any[];
                    const list = items
                      .map((it) => {
                        const s = it?.startDate ? new Date(it.startDate).getTime() : NaN;
                        const e = it?.endDate ? new Date(it.endDate).getTime() : NaN;
                        if (!Number.isFinite(s) || !Number.isFinite(e) || !(s <= now && e >= now)) return null;
                        return {
                          id: String(it?.id || ''),
                          title: String(it?.title || it?.name || 'Untitled'),
                          lane: String(it?.lane || 'General'),
                          status: prettyEnumLabel(it?.status || ''),
                          endDate: it?.endDate ? String(it.endDate).slice(0, 10) : '—',
                        };
                      })
                      .filter(Boolean) as Array<{ id: string; title: string; lane: string; status: string; endDate: string }>;

                    return list.length ? (
                      <Stack spacing={1}>
                        {list.slice(0, showAllPanel ? 1000 : 5).map((it) => (
                          <Box
                            key={it.id}
                            sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                            onClick={() => {
                              setSelectedRoadmapId(String(topPanelRoadmapId));
                              setView('timeline');
                              setTopPanel(null);
                              setTopPanelRoadmapId('');
                              requestAnimationFrame(() => timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                            }}
                          >
                            <Typography sx={{ fontWeight: 650 }} noWrap>
                              {it.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {it.lane} • {it.status || '—'} • ends {it.endDate}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info" variant="outlined">
                        No items are currently in flight for this roadmap.
                      </Alert>
                    );
                  })()
              ) : null}

              {topPanel === 'upcoming' ? (
                upcoming.length ? (
                  <Stack spacing={1}>
                    {(upcoming as any[]).slice(0, showAllPanel ? 1000 : 5).map((it: any) => {
                      const title = it.title || it.name || 'Untitled item';
                      const rmName = it?.Roadmap?.name || it?.Roadmap?.title || '—';
                      const rmId = String(it?.roadmapId || it?.Roadmap?.id || '');
                      const due = it?.endDate ? String(it.endDate).slice(0, 10) : '—';
                      return (
                        <Box
                          key={String(it.id || `${rmId}_${title}_${due}`)}
                          sx={{ p: 1, borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                          onClick={() => {
                            setView('data');
                            setMilestonesMode('upcoming');
                            if (rmId) setMilestonesRoadmapId(rmId);
                            setTopPanel(null);
                          }}
                        >
                          <Typography sx={{ fontWeight: 650 }} noWrap>
                            {title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {rmName} • due {due} • {prettyEnumLabel(it.status || '—')}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info" variant="outlined">
                    No upcoming milestones in the selected window.
                  </Alert>
                )
              ) : null}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Items by roadmap" subtitle="Total items in each roadmap" height={260}>
            <NexusBarChart
              data={itemsByRoadmap.slice(0, 8)}
              xKey="name"
              series={[{ key: 'count', name: 'Items', color: tokens.color.accent }]}
              height={220}
              showLabels
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Lanes by roadmap" subtitle="Swimlanes in use" height={260}>
            <NexusBarChart
              data={lanesByRoadmap.slice(0, 8)}
              xKey="name"
              series={[{ key: 'count', name: 'Lanes', color: tokens.color.accent }]}
              height={220}
              showLabels
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Item status mix" subtitle="Across all roadmaps" height={260}>
            <NexusDonut
              data={itemStatusMix.map((s) => ({ name: prettyEnumLabel(s.name), value: s.value }))}
              height={200}
              centerLabel="Items"
              centerValue={String(itemStatusMix.reduce((acc, s) => acc + s.value, 0))}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableCard
              title="Roadmap view"
              subtitle={view === 'timeline' ? 'Swimlane timeline (high-level)' : 'Tables (data view)'}
              actions={
                <Tabs value={view} onChange={(_e, v) => setView(v)}>
                  <Tab value="timeline" label="Timeline" />
                  <Tab value="data" label="Data" />
                </Tabs>
              }
            >
              {view === 'timeline' ? (
                <Box ref={timelineRef}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Roadmap"
                      value={selectedRoadmapId}
                      onChange={(e) => setSelectedRoadmapId(e.target.value)}
                      sx={{ minWidth: 320 }}
                    >
                      {(roadmaps as any[]).map((r: any) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField select size="small" label="Zoom" value={zoom} onChange={(e) => setZoom(e.target.value as any)} sx={{ width: 160 }}>
                      <MenuItem value="quarters">Quarters</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                    </TextField>
                    {canEdit ? (
                      <Button size="small" variant="contained" onClick={openNewItem} sx={{ borderRadius: 2 }}>
                        Add item
                      </Button>
                    ) : null}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        selectedRoadmap
                          ? `Range: ${fmtYmd((selectedRoadmap as any).startDate)} → ${fmtYmd((selectedRoadmap as any).endDate)}`
                          : `Range: ${fmtYmd(timeline.rangeStart)} → ${fmtYmd(timeline.rangeEnd)}`
                      }
                      sx={{ height: 26, fontWeight: 650 }}
                    />
                  </Box>

                  {!selectedRoadmap ? (
                    <Alert severity="info" variant="outlined">
                      Create a roadmap and add a few items to see the timeline.
                    </Alert>
                  ) : (
                    <Box
                      sx={{
                        border: `1px solid ${tokens.color.border}`,
                        borderRadius: `${tokens.radius.md}px`,
                        overflow: 'hidden',
                        background: tokens.color.surface,
                      }}
                    >
                      {/* SINGLE horizontal scroll for header + lanes */}
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 180 + timeline.totalWidthPx }}>
                          {/* header */}
                          <Box
                            sx={{
                              display: 'flex',
                              borderBottom: `1px solid ${tokens.color.border}`,
                              background: tokens.color.surfaceMuted,
                            }}
                          >
                            <Box
                              sx={{
                                width: 180,
                                p: 1,
                                fontWeight: 650,
                                color: tokens.color.text,
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                background: tokens.color.surface,
                                borderRight: `1px solid ${tokens.color.border}`,
                              }}
                            >
                              Lane
                            </Box>
                            <Box sx={{ display: 'flex', width: timeline.totalWidthPx }}>
                              {timeline.segs.map((s) => (
                                <Box
                                  key={`${s.label}-${String(s.start)}`}
                                  sx={{
                                    width: timeline.segWidth,
                                    px: 1.25,
                                    py: 1,
                                    borderLeft: `1px solid ${tokens.color.border}`,
                                    color: tokens.color.textSecondary,
                                    fontWeight: 650,
                                    fontSize: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: 999,
                                        background: tokens.color.accent,
                                      }}
                                    />
                                    <span>{s.label}</span>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* lanes (no internal vertical scroll; let page scroll) */}
                          <Box>
                        {timeline.lanes.length ? (
                          timeline.lanes.map((lane) => {
                            const ROW_H = 60;
                            const BAR_H = 38;
                            const PAD_Y = 14;
                            const laneHeight = PAD_Y * 2 + lane.rowCount * ROW_H;

                            return (
                              <Box
                                key={lane.lane}
                                sx={{
                                  display: 'flex',
                                  borderBottom: `1px solid ${tokens.color.border}`,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 180,
                                    p: 1,
                                    height: laneHeight,
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    background: tokens.color.surface,
                                    borderRight: `1px solid ${tokens.color.border}`,
                                  }}
                                >
                                  <Typography sx={{ fontWeight: 650 }} noWrap title={lane.lane}>
                                    {lane.lane}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    position: 'relative',
                                    height: laneHeight,
                                    width: timeline.totalWidthPx,
                                  }}
                                >
                                  {/* grid lines */}
                                  {timeline.segs.map((s, idx) => (
                                    <Box
                                      key={`${s.label}-${idx}`}
                                      sx={{
                                        position: 'absolute',
                                        left: idx * timeline.segWidth,
                                        top: 0,
                                        bottom: 0,
                                        width: timeline.segWidth,
                                        borderLeft: `1px solid ${tokens.color.border}`,
                                      }}
                                    />
                                  ))}

                                  {lane.items.map((packed: any) => {
                                    const it = packed.it as any as RoadmapItemListItem;
                                    const s = Number(packed.startMs);
                                    const e = Number(packed.endMs);
                                    const row = Number(packed.row) || 0;
                                    const leftPx = clamp(((s - timeline.msStart) / timeline.span) * timeline.totalWidthPx, 0, timeline.totalWidthPx - 12);
                                    const widthPx = clamp(((e - s) / timeline.span) * timeline.totalWidthPx, 28, timeline.totalWidthPx - leftPx);
                                    const color = it.color || tokens.color.accent;
                                    const showStatus = widthPx >= 180;
                                    const top = PAD_Y + row * ROW_H + (ROW_H - BAR_H) / 2;

                                    return (
                                      <Box
                                        key={it.id + ':' + row}
                                        onClick={() => (canEdit ? openEditItem(it) : undefined)}
                                        title={it.title}
                                        sx={{
                                          position: 'absolute',
                                          top,
                                          left: leftPx,
                                          width: widthPx,
                                          height: BAR_H,
                                          borderRadius: `${tokens.radius.sm}px`,
                                          cursor: canEdit ? 'pointer' : 'default',
                                          backgroundColor: color,
                                          border: `1px solid ${tokens.color.border}`,
                                          px: 1,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                          overflow: 'hidden',
                                        }}
                                      >
                                        <Typography sx={{ fontWeight: 650, fontSize: 12, position: 'relative', color: '#FFFFFF' }} noWrap>
                                          {it.title}
                                        </Typography>
                                        {showStatus && it.status ? (
                                          <Chip
                                            size="small"
                                            label={prettyEnumLabel(it.status)}
                                            sx={{ height: 20, fontWeight: 650, backgroundColor: 'rgba(255,255,255,0.85)', position: 'relative' }}
                                          />
                                        ) : null}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })
                        ) : (
                          <Box sx={{ p: 2 }}>
                            <Alert severity="info" variant="outlined">
                              No items yet. Add a few milestones/workstreams to see the high-level roadmap.
                            </Alert>
                          </Box>
                        )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <div style={{ width: '100%', height: 520 }}>
                      <ResizableDataGrid
                        storageKey="gridWidths.roadmaps.roadmaps"
                        enableDensityControls
                        rows={roadmaps}
                        columns={roadmapCols}
                        getRowId={(r) => r.id}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <TableCard
                      title="Milestones"
                      subtitle={milestonesMode === 'upcoming' ? 'Items with an end date in the selected window.' : 'All roadmap items (milestones + workstreams).'}
                      actions={
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <TextField
                            select
                            size="small"
                            label="Roadmap"
                            value={milestonesRoadmapId}
                            onChange={(e) => setMilestonesRoadmapId(e.target.value)}
                            sx={{ minWidth: 240 }}
                          >
                            <MenuItem value="">
                              <em>All roadmaps</em>
                            </MenuItem>
                            {(roadmaps as any[]).map((r: any) => (
                              <MenuItem key={r.id} value={String(r.id)}>
                                {String(r.name || r.title || r.id)}
                              </MenuItem>
                            ))}
                          </TextField>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={milestonesMode}
                            onChange={(_, v) => v && setMilestonesMode(v)}
                            sx={{ borderRadius: 2 }}
                          >
                            <ToggleButton value="upcoming">Upcoming</ToggleButton>
                            <ToggleButton value="all">All</ToggleButton>
                          </ToggleButtonGroup>
                          {canEdit ? (
                            <Button size="small" variant="contained" onClick={openNewItem} sx={{ borderRadius: 2 }}>
                              Add milestone
                            </Button>
                          ) : null}
                        </Box>
                      }
                    >
                      <div style={{ width: '100%', height: 520 }}>
                        <ResizableDataGrid
                          storageKey="gridWidths.roadmaps.milestones"
                          enableDensityControls
                          rows={milestonesMode === 'upcoming' ? upcoming : allMilestonesRows}
                          columns={milestoneCols}
                          getRowId={(r) => r.id}
                          disableRowSelectionOnClick
                          pageSizeOptions={[10, 25, 50]}
                          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                        />
                      </div>
                    </TableCard>
                  </Grid>
                </Grid>
              )}
            </TableCard>
          </Grid>
        </Grid>
      )}

      <Dialog open={roadmapDialogOpen} onClose={() => setRoadmapDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editingRoadmap ? 'Edit roadmap' : 'New roadmap'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField label="Name" value={roadmapForm.name} onChange={(e) => setRoadmapForm((v) => ({ ...v, name: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" value={roadmapForm.description} onChange={(e) => setRoadmapForm((v) => ({ ...v, description: e.target.value }))} fullWidth multiline minRows={3} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Portfolio" value={roadmapForm.portfolioId} onChange={(e) => setRoadmapForm((v) => ({ ...v, portfolioId: e.target.value }))} fullWidth>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {portfolios.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="View type" value={roadmapForm.viewType} onChange={(e) => setRoadmapForm((v) => ({ ...v, viewType: e.target.value }))} fullWidth>
                <MenuItem value="timeline">timeline</MenuItem>
                <MenuItem value="quarters">quarters</MenuItem>
                <MenuItem value="months">months</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="Start" value={roadmapForm.startDate} onChange={(e) => setRoadmapForm((v) => ({ ...v, startDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="End" value={roadmapForm.endDate} onChange={(e) => setRoadmapForm((v) => ({ ...v, endDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRoadmapDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={saveRoadmap} variant="contained" disabled={savingRoadmap || !roadmapForm.name.trim()} sx={{ borderRadius: 2 }}>
            {savingRoadmap ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editingItem ? 'Edit milestone' : 'New milestone'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField select label="Roadmap" value={itemForm.roadmapId} onChange={(e) => setItemForm((v) => ({ ...v, roadmapId: e.target.value }))} fullWidth>
                {roadmaps.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {(r as any).name || (r as any).title || r.id}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Title" value={itemForm.title} onChange={(e) => setItemForm((v) => ({ ...v, title: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" value={itemForm.description} onChange={(e) => setItemForm((v) => ({ ...v, description: e.target.value }))} fullWidth multiline minRows={3} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Project (optional)" value={itemForm.projectId} onChange={(e) => setItemForm((v) => ({ ...v, projectId: e.target.value }))} fullWidth>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Lane"
                value={laneCreateMode ? NEW_LANE_VALUE : (itemForm.lane || laneOptions[0] || 'General')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === NEW_LANE_VALUE) {
                    setLaneCreateMode(true);
                    setNewLaneName('');
                    setItemForm((prev) => ({ ...prev, lane: '' }));
                    return;
                  }
                  setLaneCreateMode(false);
                  setNewLaneName('');
                  setItemForm((prev) => ({ ...prev, lane: v }));
                }}
                fullWidth
              >
                {laneOptions.map((l) => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={NEW_LANE_VALUE}>
                  <em>Create new lane…</em>
                </MenuItem>
              </TextField>
            </Grid>
            {laneCreateMode ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New lane"
                  placeholder="e.g. Platform, Data, EHR…"
                  value={newLaneName}
                  onChange={(e) => {
                    const name = e.target.value;
                    setNewLaneName(name);
                    setItemForm((prev) => ({ ...prev, lane: name }));
                  }}
                  fullWidth
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} />
            )}
            <Grid item xs={12} sm={6}>
              <TextField label="Status" value={itemForm.status} onChange={(e) => setItemForm((v) => ({ ...v, status: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Color" value={itemForm.color} onChange={(e) => setItemForm((v) => ({ ...v, color: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: 999,
                    backgroundColor: normalizeHexColor(itemForm.color || '') || tokens.color.border,
                    border: `1px solid ${tokens.color.borderStrong}`,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
                  Recommended:
                </Typography>
                {DEFAULT_COLORS.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setItemForm((v) => ({ ...v, color: c }))}
                    title={c}
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      backgroundColor: c,
                      border: `1px solid ${tokens.color.borderStrong}`,
                      cursor: 'pointer',
                    }}
                  />
                ))}
                {recentColors.length ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650, ml: 1 }}>
                      Recent:
                    </Typography>
                    {recentColors.slice(0, 6).map((c) => (
                      <Box
                        key={c}
                        onClick={() => setItemForm((v) => ({ ...v, color: c }))}
                        title={c}
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          backgroundColor: c,
                          border: `1px solid ${tokens.color.borderStrong}`,
                          cursor: 'pointer',
                        }}
                      />
                    ))}
                  </>
                ) : null}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="Start" value={itemForm.startDate} onChange={(e) => setItemForm((v) => ({ ...v, startDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="End" value={itemForm.endDate} onChange={(e) => setItemForm((v) => ({ ...v, endDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setItemDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={saveItem} variant="contained" disabled={savingItem || !itemForm.title.trim() || !itemForm.roadmapId} sx={{ borderRadius: 2 }}>
            {savingItem ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      </Container>
    </PageLoader>
  );
};

export default RoadmapsPage;
